
export default function HoldingCostsSection() {
  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            The Hidden Cost of Holding Land
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Think vacant land is "free" to own? Think again. Here's what holding onto unwanted land really costs you over time.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow-2xl p-8 mb-12">
          <h3 className="text-3xl font-bold mb-8 text-gray-900 text-center">10-Year Cost Analysis: Holding vs. Selling</h3>
          
          <div className="overflow-x-auto mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-900 text-white">
                  <th className="px-6 py-4 text-left">Expense Category</th>
                  <th className="px-6 py-4 text-right">Annual Cost</th>
                  <th className="px-6 py-4 text-right">10-Year Total</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-semibold text-gray-900">Property Taxes</td>
                  <td className="px-6 py-4 text-right text-gray-700">$800</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">$8,000</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Liability Insurance</td>
                  <td className="px-6 py-4 text-right text-gray-700">$300</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">$3,000</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-semibold text-gray-900">HOA Fees (if applicable)</td>
                  <td className="px-6 py-4 text-right text-gray-700">$200</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">$2,000</td>
                </tr>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">Maintenance/Clearing</td>
                  <td className="px-6 py-4 text-right text-gray-700">$400</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">$4,000</td>
                </tr>
                <tr className="border-b border-gray-200">
                  <td className="px-6 py-4 font-semibold text-gray-900">Opportunity Cost (5% return)</td>
                  <td className="px-6 py-4 text-right text-gray-700">$2,500</td>
                  <td className="px-6 py-4 text-right font-bold text-gray-900">$25,000</td>
                </tr>
                <tr className="bg-teal-600 text-white font-bold">
                  <td className="px-6 py-4">TOTAL COST OF HOLDING</td>
                  <td className="px-6 py-4 text-right">$4,200/year</td>
                  <td className="px-6 py-4 text-right text-2xl">$42,000</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-lg">
            <h4 className="font-bold text-gray-900 mb-2 text-xl">The Shocking Reality:</h4>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you own $50,000 worth of vacant land and hold it for 10 years, you'll spend approximately $42,000 in direct costs and opportunity costs. That's 84% of the land's value consumed by holding expenses!
            </p>
            <p className="text-gray-700 leading-relaxed">
              By selling today for $50,000 cash and investing that money at a conservative 5% annual return, you'd have $81,444 after 10 years. That's $39,444 more than holding the land - and that's assuming the land doesn't decrease in value.
            </p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-lg mb-6">
              <i className="ri-money-dollar-circle-line text-red-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Property Taxes Never Stop</h3>
            <p className="text-gray-700 leading-relaxed">
              In El Paso County, property taxes on vacant land typically range from $500-$1,500 per year depending on location and acreage. These taxes increase annually and must be paid whether you use the land or not. Fall behind, and penalties compound at 12% per year.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-orange-100 rounded-lg mb-6">
              <i className="ri-shield-line text-orange-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Liability Risk</h3>
            <p className="text-gray-700 leading-relaxed">
              If someone gets injured on your vacant land - whether trespassing or not - you could be liable. Liability insurance for vacant land costs $200-$500 annually. Without it, you're exposed to potentially devastating lawsuits from accidents, illegal dumping, or unauthorized use.
            </p>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-lg">
            <div className="w-16 h-16 flex items-center justify-center bg-yellow-100 rounded-lg mb-6">
              <i className="ri-plant-line text-yellow-600 text-3xl"></i>
            </div>
            <h3 className="text-xl font-bold mb-4 text-gray-900">Maintenance Costs</h3>
            <p className="text-gray-700 leading-relaxed">
              Vacant land doesn't maintain itself. Weeds, trash, and overgrowth accumulate. Many counties and HOAs require regular maintenance. Clearing and mowing can cost $300-$1,000 per visit. Ignore it, and you'll face code violations and fines.
            </p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-12 text-white text-center">
          <h3 className="text-3xl font-bold mb-4">Stop the Financial Drain Today</h3>
          <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed text-teal-50">
            Every month you hold unwanted land, you're losing money. Convert your land to cash now and put that money to work in investments that actually generate returns instead of consuming them.
          </p>
          <a href="tel:+1-915-206-5814" className="inline-flex items-center space-x-2 px-8 py-4 bg-white text-teal-600 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-phone-fill"></i>
            <span>Get Your Cash Offer: (915) 206-5814</span>
          </a>
        </div>
      </div>
    </section>
  );
}
